import { ProcessCellForExportParams, StatusPanelDef } from 'ag-grid-community';
import { mapKeys, mapValues } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuthUser, useUserPermissions } from 'auth/hooks';
import { AgGridServerTable } from 'core/components';
import { TotalsStatusBar } from 'core/elements';
import { NotificationPosition, TableLayoutType } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { AgGridServerTableProvider } from 'core/states';
import { AgGridGetRowsParams } from 'core/types';
import { IJob } from 'jobs/types';
import { DetachQuoteButton } from 'projects/components';
import { BulkExportSkuPartsButton, BulkSendAsSpaButton } from 'quotes/components/ExportSkuPart';
import { ImportVendorQuoteButton } from 'quotes/components/ImportVendorQuote';
import { ArchiveQuoteButton } from 'quotes/components/QuoteList';
import { AddVendorQuoteButton } from 'quotes/components/VendorQuoteUpsert';
import {
  QuoteEntityType,
  QuoteListType,
  QuoteState,
  QuoteStatus,
  QuoteStatusLabel,
  QuoteType,
  VendorQuoteAttachToType,
} from 'quotes/enums';
import { VendorQuoteHelper } from 'quotes/helpers';
import { quotePartsPath } from 'quotes/routes/routePaths';
import { quoteService } from 'quotes/services';

import { getColumnDef } from './columns';
import { VendorQuoteColId } from './enums';
import { getColumnGates, getInitialState, getVendorDisplayName } from './utils';

interface IProps {
  entityType?: QuoteEntityType;
  isDtdProject?: boolean;
  job?: IJob;
  projectId?: number;
  quoteListType?: QuoteListType;
}

export const VendorQuoteList = ({
  entityType = QuoteEntityType.QUOTE,
  isDtdProject = false,
  job,
  projectId,
  quoteListType = QuoteListType.GLOBAL,
}: IProps) => {
  const { user } = useAuthUser();
  const { hasPermission } = useUserPermissions();

  const { t } = useTranslation();
  const navigate = useNavigate();

  // Global gate (not per-row): the company-level alternate-export capability. No per-quote status
  // check (isSkuExportAvailable) — there is no single quote in scope here, so the bulk-action
  // visibility hinges only on the company capability.
  const showSkuExportActions = useMemo(
    () => VendorQuoteHelper.isAlternateExportEnabled({ user }),
    [user]
  );

  // The supplier column is an alternate-platform concern only.
  const showSupplierInfo = useMemo(
    () => AlternateVersionHelper.isAlternateVersionEnabled({ user }),
    [user]
  );

  const { showFilenetStatus, showTradeAgreementInfo } = getColumnGates({
    isAlternateVersionEnabled: showSupplierInfo,
    isAlternateExportEnabled: showSkuExportActions,
    hasPermission,
  });

  const columnDefs = useMemo(
    () =>
      getColumnDef(entityType, {
        quoteListType,
        showSupplierInfo,
        showFilenetStatus,
        showTradeAgreementInfo,
      }),
    [entityType, quoteListType, showSupplierInfo, showFilenetStatus, showTradeAgreementInfo]
  );

  const initialState = useMemo(() => getInitialState(user, entityType), [entityType, user]);

  const tableId = useMemo(() => `vendor${entityType}ListTable`, [entityType]);

  const tableEntityType = useMemo(() => `Vendor${entityType}`, [entityType]);

  const layoutType = useMemo(() => {
    const isQuote = entityType === QuoteEntityType.QUOTE;

    if (quoteListType === QuoteListType.GLOBAL) {
      return isQuote ? TableLayoutType.VENDOR_QUOTE : TableLayoutType.VENDOR_ORDER;
    }

    return isQuote ? TableLayoutType.PROJECT_VENDOR_QUOTE : TableLayoutType.PROJECT_VENDOR_ORDER;
  }, [entityType, quoteListType]);

  const upsertVendorQuoteDefaultValues = useMemo(() => {
    switch (quoteListType) {
      case QuoteListType.PROJECT:
        return { project: { id: projectId } };

      case QuoteListType.JOB:
        if (!job) {
          return {};
        }

        return {
          project: { id: job.projectId },
          jobName: job.name,
        };

      default:
        return {};
    }
  }, [quoteListType, projectId, job]);

  const importVendorQuoteDefaultValues = useMemo(() => {
    switch (quoteListType) {
      case QuoteListType.PROJECT:
        return {
          project: { id: projectId },
          attachTo: VendorQuoteAttachToType.PROJECT,
        };

      case QuoteListType.JOB:
        if (!job) {
          return {};
        }

        return {
          project: { id: job.projectId },
          jobName: job.name,
          attachTo: VendorQuoteAttachToType.JOB,
        };

      default:
        return {};
    }
  }, [quoteListType, projectId, job]);

  const mapFilters = useCallback((filters) => {
    let mappedFilters = filters;

    mappedFilters = mapKeys(mappedFilters, (_, key) => {
      switch (key) {
        case 'vendorContactName':
          return 'vendorContactUserId';
        default:
          return key;
      }
    });

    mappedFilters = mapValues(mappedFilters, (value, key) => {
      switch (key) {
        case 'archived':
          return 1 === value.length ? (value[0] === QuoteState.ACTIVE ? 0 : 1) : null;
        case 'status':
          return value.join('|');
        case 'tradeAgreementStatus':
          return value.join('|');
        default:
          return value;
      }
    });

    return mappedFilters;
  }, []);

  const getRows = useCallback(
    ({ filters, ...rest }: AgGridGetRowsParams) =>
      quoteService.getVendorQuotes({
        filters: {
          ...mapFilters(filters),
          isOrder: QuoteEntityType.ORDER === entityType ? 1 : 0,
          ...(job?.id ? { bomId: job.id } : {}),
          ...(projectId ? { projectId } : {}),
        },
        ...rest,
      }),
    [entityType, job, mapFilters, projectId]
  );

  const statusBar = useMemo<{
    statusPanels: StatusPanelDef[];
  }>(
    () => ({
      statusPanels: [
        {
          statusPanel: TotalsStatusBar,
          statusPanelParams:
            QuoteEntityType.ORDER === entityType ? { totalLabel: t('labelOrderValue') } : null,
          align: 'left',
        },
      ],
    }),
    [entityType, t]
  );

  const excelExportParams = useMemo(
    () => ({
      processCellCallback: (params: ProcessCellForExportParams) => {
        const colId = params.column.getColId();
        if (colId === VendorQuoteColId.ARCHIVED) {
          return params.value === 0 ? t('labelActive') : t('labelArchived');
        }

        if (colId === VendorQuoteColId.STATUS) {
          return QuoteStatusLabel[params.value as QuoteStatus];
        }

        if (colId === VendorQuoteColId.VENDOR_NAME) {
          return getVendorDisplayName(params.node?.data) ?? params.value;
        }

        return params.value;
      },
    }),
    [t]
  );

  const getHiddenFields = useCallback(() => {
    switch (quoteListType) {
      case QuoteListType.PROJECT:
        return {
          project: true,
        };
      case QuoteListType.JOB:
        return {
          project: true,
          jobName: true,
        };
      default:
        return {};
    }
  }, [quoteListType]);

  const actionButtons = useMemo(() => {
    const actionButtons = [
      <AddVendorQuoteButton
        key="addVendorQuoteButton"
        entityType={entityType}
        defaultValues={upsertVendorQuoteDefaultValues}
        hiddenFields={getHiddenFields()}
      />,
    ];

    if (QuoteEntityType.QUOTE === entityType && !isDtdProject) {
      actionButtons.push(
        <ImportVendorQuoteButton
          key="importVendorQuote"
          notificationPosition={NotificationPosition.TOP}
          onImportComplete={(quoteId) => navigate(quotePartsPath(quoteId))}
          defaultValues={importVendorQuoteDefaultValues}
          hiddenFields={getHiddenFields()}
        />
      );
    }

    return actionButtons;
  }, [
    entityType,
    getHiddenFields,
    importVendorQuoteDefaultValues,
    isDtdProject,
    navigate,
    upsertVendorQuoteDefaultValues,
  ]);

  const selectionActionButtons = useMemo(() => {
    if (QuoteEntityType.ORDER === entityType) {
      return [];
    }

    const selectionActionButtons = [
      <ArchiveQuoteButton
        key="archiveQuote"
        entityType={entityType}
        quoteType={QuoteType.VENDOR}
      />,
    ];

    if (QuoteListType.PROJECT === quoteListType) {
      selectionActionButtons.push(
        <DetachQuoteButton key="detachQuoteButton" projectId={projectId} />
      );
    }

    // Bulk SKU export of selected Imported Alternate VQs. Company-capability gated here; the button
    // self-disables until >= 1 selected row is an eligible imported alternate VQ. Bulk Send as SPA
    // sits alongside and additionally self-hides on the Send as SPA role resource.
    if (showSkuExportActions) {
      selectionActionButtons.push(<BulkExportSkuPartsButton key="bulkExportSkuParts" />);
      selectionActionButtons.push(<BulkSendAsSpaButton key="bulkSendAsSpa" />);
    }

    return selectionActionButtons;
  }, [entityType, projectId, quoteListType, showSkuExportActions]);

  return (
    <AgGridServerTableProvider
      key={user?.id}
      tableId={tableId}
      entityType={tableEntityType}
      columnDefs={columnDefs}
      statusBar={statusBar}
      initialState={initialState}
      getRows={getRows}
      hasRowSelection={selectionActionButtons.length > 0}
      hasLayoutActions
      hasManageFilters
      layoutType={layoutType}
      excelExportParams={excelExportParams}
    >
      <AgGridServerTable
        actionButtons={actionButtons}
        selectionActionButtons={selectionActionButtons}
      />
    </AgGridServerTableProvider>
  );
};
